import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Spinner,
  Alert,
  Button,
  Accordion,
  Breadcrumb,
  Pagination,
  InputGroup,
  Form,
  Tabs,
  Tab,
  Modal
} from 'react-bootstrap';
import {
  FaHistory,
  FaCalendarAlt,
  FaRupeeSign,
  FaCreditCard,
  FaFileInvoice,
  FaCheckCircle,
  FaExclamationTriangle,
  FaArrowLeft,
  FaEye,
  FaDownload,
  FaSync,
  FaSearch,
  FaFilter,
  FaReceipt,
  FaMoneyBillWave,
  FaClock,
  FaFilePdf,
  FaPrint,
  FaSchool,
  FaListOl,
  FaStream,
  FaTags,
  FaUser,
  FaPhone,
  FaMapMarkerAlt
} from 'react-icons/fa';
import Header from './Header';
import { ProductContext } from '../context/AppContext';
import Swal from 'sweetalert2';
import moment from 'moment';
import { getCachedData, setCachedData } from '../utils/apiCache';

const FeeHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getRegistration, userData, Automationdata, GetstudentData } = useContext(ProductContext);
  const [historyData, setHistoryData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('payment');
  const [expandedRows, setExpandedRows] = useState([]);
  const authtoken = localStorage.getItem('token');
  const regsId = getRegistration?.data?.id || userData?.userId;
  const schoolInfo = Automationdata?.data?.[0] || {};
  const [showModal, setShowModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  const studentId = location.state?.studentId || localStorage.getItem('student_id') || regsId;
  const activeSessionId = GetstudentData?.session || getRegistration?.data?.session || null;
    const classSectionData = `${GetstudentData?.cls_sec?.cls?.Classes || "" } - ${GetstudentData?.cls_sec?.section?.section || ""}`;

  

  const fetchFeeHistory = async () => {
    if (!studentId || !authtoken || !activeSessionId) {
      setError('Student ID, Session, or authentication token not found.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        `https://api.mirai.healthcare/api/fee-service/fee-details/history-data?session_id=${activeSessionId}&student_id=${studentId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'x-auth': authtoken,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      let historyArray = [];
      let adjustmentsAndRefunds = [];

      const transactions = {};
      if (data && Array.isArray(data.fee_details)) {
        data.fee_details.forEach(feeDetail => {
          (feeDetail.collections || []).forEach(collection => {
            let audits = [];
            if (Array.isArray(collection.fee_coll_audit_new)) {
              if (collection.fee_coll_audit_new.length === 0) return;
              audits = collection.fee_coll_audit_new;
            } else if (Array.isArray(collection.fee_coll_audit)) {
              if (collection.fee_coll_audit.length === 0) return;
              audits = collection.fee_coll_audit;
            } else {
              const paidAmt = parseFloat(collection.amount_paid || 0) || parseFloat(collection.amt_paid || 0);
              if (paidAmt === 0 && collection.payment_status === 'Pending') return;
              audits = [collection];
            }

            audits.forEach((audit, auditIndex) => {
              const auditHeader = audit?.fee_coll_header || {};
              if (auditHeader.is_deleted === true) return;

              const headerId =
                auditHeader.id ||
                audit?.fee_coll_header_id ||
                collection.fee_coll_header_id ||
                collection.id;
              if (!headerId) return;

              if (!transactions[headerId]) {
                transactions[headerId] = {
                  id: headerId,
                  invoice_no: auditHeader.invoice_no || collection.invoice_no || `INV-${headerId}`,
                  payment_mode: auditHeader.payment_mode || audit?.payment_mode || collection.payment_mode || '--',
                  payment_date: auditHeader.payment_date || audit?.payment_date || collection.payment_date || null,
                  remarks: auditHeader.remarks || audit?.remarks || collection.remarks || '',
                  transaction_no: audit?.transaction_no || collection.transaction_no || null,
                  cheque_no: collection.cheque_no || null,
                  bank_name: collection.bank_name || null,
                  status: 'payment',
                  fee_coll_history: [],
                };
              }

              const lineId = audit?.id || `${collection.id || headerId}-${auditIndex}`;
              const isDuplicate = transactions[headerId].fee_coll_history.some(c => c.id === lineId);
              if (!isDuplicate) {
                const payableAmount = parseFeeAmount(
                  feeDetail?.payable ??
                  feeDetail?.fee_configuration?.amount ??
                  audit?.amount_due ??
                  collection?.amount_due ??
                  0
                );
                const paidThisReceipt = parseFeeAmount(
                  audit?.amt_paid ??
                  collection?.amt_paid ??
                  audit?.amount_paid ??
                  collection?.amount_paid ??
                  0
                );
                const paidTillReceipt = parseFeeAmount(
                  audit?.amount_paid ??
                  collection?.amount_paid ??
                  paidThisReceipt
                );
                const detailBalanceDue = Math.max(payableAmount - paidTillReceipt, 0);
                const amountBalanced = detailBalanceDue;

                transactions[headerId].fee_coll_history.push({
                  id: lineId,
                  ...collection,
                  payable: payableAmount,
                  amount_due: payableAmount,
                  amount_paid: paidTillReceipt,
                  amt_paid: paidThisReceipt,
                  paid_till_receipt: paidTillReceipt,
                  paid_this_receipt: paidThisReceipt,
                  amount_balanced: amountBalanced,
                  detail_balance_due: detailBalanceDue,
                  fee_coll: {
                    ...collection,
                    fee_month: Number(feeDetail.month),
                    fee_ctgry: {
                      category_name: feeDetail.fee_configuration?.category_name || 'Fee',
                    },
                    payable: payableAmount,
                    amount_due: payableAmount,
                    amount_paid: paidTillReceipt,
                    amt_paid: paidThisReceipt,
                    paid_till_receipt: paidTillReceipt,
                    paid_this_receipt: paidThisReceipt,
                    amount_balanced: amountBalanced,
                    detail_balance_due: detailBalanceDue,
                  },
                  student_discount_approval: feeDetail.student_discount_approval,
                });
              }
            });
          });

          // NOTE:
          // Do not create synthetic ADJ-* timeline rows from discount approvals.
          // Adjustments are reflected in the related payment records directly.

          // Process Refunds
          if (Array.isArray(feeDetail.refund)) {
            feeDetail.refund.forEach(ref => {
              adjustmentsAndRefunds.push({
                  id: `ref_${ref.id}`,
                  invoice_no: `REF-${ref.id}`,
                  payment_mode: 'refund',
                  status: 'refund',
                  payment_date: ref.created_at,
                  amount: ref.amount,
                  remarks: ref.remarks || 'Refund',
                  fee_refund: {
                    refund_audit: [{
                      type: 'refund',
                      fee_ctgry: { category_name: feeDetail.fee_configuration?.category_name },
                      fee_month: feeDetail.month,
                      amount: ref.amount
                    }]
                  }
              });
            });
          }
        });
      }

      const normalizedTransactions = Object.values(transactions).map((transaction) => {
        const computedAmount = transaction.fee_coll_history.reduce(
          (sum, coll) => sum + getCollectionPaid(coll),
          0
        );
        return {
          ...transaction,
          amount: computedAmount,
        };
      }).filter(transaction => (transaction.fee_coll_history?.length || 0) > 0);

      historyArray = [...normalizedTransactions, ...adjustmentsAndRefunds].filter((item) => {
        const invoiceNo = (item.invoice_no || "").toUpperCase();
        const status = (item.status || "").toLowerCase();
        const paymentMode = (item.payment_mode || "").toLowerCase();
        const isAdjustmentEntry =
          invoiceNo.startsWith("ADJ-") || status === "adjust" || paymentMode === "adjust";
        return !isAdjustmentEntry;
      });

      const sortedData = historyArray.sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date));
      setHistoryData(sortedData);
      setFilteredData(sortedData);
    } catch (err) {
      console.error('Error fetching fee history:', err);
      setError(err.message || 'Failed to fetch fee history.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (studentId && activeSessionId) {
      fetchFeeHistory();
    } else {
      setError('Student ID not provided. Redirecting to Fee Collection...');
      setTimeout(() => navigate('/fee-collection'), 2000);
    }
  }, [studentId, authtoken, activeSessionId]);

  // Filter data based on search and status
  useEffect(() => {
    let filtered = historyData;

    // Tab filter
    if (activeTab === 'payment') {
      filtered = filtered.filter(item => 
        !['refund', 'adjust'].includes(item.payment_mode?.toLowerCase()) && 
        !['refund', 'adjust'].includes(item.status?.toLowerCase())
      );
    } else if (activeTab === 'refund') {
      filtered = filtered.filter(item => 
        item.payment_mode?.toLowerCase() === 'refund' || 
        item.status?.toLowerCase() === 'refund'
      );
    } else if (activeTab === 'adjustment') {
      filtered = filtered.filter(item => 
        item.payment_mode?.toLowerCase() === 'adjust' || 
        item.status?.toLowerCase() === 'adjust'
      );
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.invoice_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.payment_mode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.remarks?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => getOverallStatus(item) === statusFilter);
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, historyData, activeTab]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(parseFloat(amount || 0));
  };

  const formatDate = (dateString) => {
    return dateString ? moment(dateString).format('DD MMM YYYY') : '--';
  };

  const getMonthName = (monthNumber) => {
    if (!monthNumber) return '';
    return moment().month(monthNumber - 1).format('MMMM');
  };

  const formatDateTime = (dateString) => {
    return dateString ? moment(dateString).format('DD MMM YYYY, hh:mm A') : '--';
  };

  const parseFeeAmount = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getCollectionDue = (collection = {}) =>
    parseFeeAmount(
      collection?.payable ??
      collection?.fee_coll?.payable ??
      collection?.amount_due ??
      collection?.fee_coll?.amount_due ??
      0
    );

  const getCollectionPaid = (collection = {}) =>
    parseFeeAmount(
      collection?.paid_this_receipt ??
      collection?.fee_coll?.paid_this_receipt ??
      collection?.amt_paid ??
      collection?.fee_coll?.amt_paid ??
      collection?.amount_paid ??
      collection?.fee_coll?.amount_paid ??
      0
    );

  const getCollectionPaidTillReceipt = (collection = {}) =>
    parseFeeAmount(
      collection?.paid_till_receipt ??
      collection?.fee_coll?.paid_till_receipt ??
      collection?.amount_paid ??
      collection?.fee_coll?.amount_paid ??
      getCollectionPaid(collection)
    );

  const getCollectionBalance = (collection = {}) => {
    const due = getCollectionDue(collection);
    const paidTill = getCollectionPaidTillReceipt(collection);
    if (due > 0) {
      return Math.max(due - paidTill, 0);
    }

    const detailBalance = collection?.detail_balance_due ?? collection?.fee_coll?.detail_balance_due;
    if (detailBalance !== null && detailBalance !== undefined && detailBalance !== '') {
      return parseFeeAmount(detailBalance);
    }
    const explicitBalance = collection?.amount_balanced ?? collection?.fee_coll?.amount_balanced;
    if (explicitBalance !== null && explicitBalance !== undefined && explicitBalance !== '') {
      return parseFeeAmount(explicitBalance);
    }
    return Math.max(0, getCollectionDue(collection) - getCollectionPaidTillReceipt(collection));
  };

  const formatNumber = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parseFloat(amount || 0));
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      'Paid': { variant: 'success', icon: FaCheckCircle },
      'Partially Paid': { variant: 'warning', icon: FaClock },
      'Pending': { variant: 'secondary', icon: FaExclamationTriangle },
      'Refunded': { variant: 'info', icon: FaArrowLeft },
      'Adjusted': { variant: 'primary', icon: FaSync }
    };

    const config = statusConfig[status] || { variant: 'secondary', icon: FaExclamationTriangle };
    const IconComponent = config.icon;

    return (
      <Badge bg={config.variant} className="d-flex align-items-center gap-1 px-2 py-1" style={{ width: 'fit-content' }}>
        <IconComponent size={12} />
        {status}
      </Badge>
    );
  };

  const calculateTotalPaid = (historyItem) => {
    return historyItem.fee_coll_history.reduce((total, item) => total + getCollectionPaid(item), 0);
  };

  const getApprovedDiscountForCollection = (collection = {}) => {
    const approvals = Array.isArray(collection.student_discount_approval)
      ? collection.student_discount_approval
      : [];

    return approvals.reduce((sum, approval) => {
      const isApproved = approval?.status === 'Approved';
      const sameMonth = Number(approval?.month) === Number(collection?.fee_coll?.fee_month);
      const sameCategory = Number(approval?.fee_category_id) === Number(collection?.fee_category_id);

      if (!isApproved || !sameMonth || !sameCategory) return sum;
      return sum + parseFloat(approval?.amount || 0);
    }, 0);
  };

  const hasApprovedDiscountEntry = (collection = {}) => {
    const approvals = Array.isArray(collection.student_discount_approval)
      ? collection.student_discount_approval
      : [];
    return approvals.some((approval) => {
      const isApproved = approval?.status === 'Approved';
      const sameMonth = Number(approval?.month) === Number(collection?.fee_coll?.fee_month);
      const sameCategory = Number(approval?.fee_category_id) === Number(collection?.fee_category_id);
      return isApproved && sameMonth && sameCategory;
    });
  };

  const getFeeItemMonth = (collection = {}) => {
    return Number(collection?.fee_coll?.fee_month || collection?.fee_month || 0);
  };

  const buildMonthLabel = (monthNumbers = []) => {
    if (!monthNumbers.length) return '--';
    return monthNumbers.map((month) => getMonthName(month)).filter(Boolean).join(', ');
  };

  const getConsolidatedFeeItems = (feeItems = []) => {
    const groupedItems = feeItems.reduce((acc, coll) => {
      const categoryName = coll.fee_coll?.fee_ctgry?.category_name || coll.description || 'Fee Item';
      const due = getCollectionDue(coll);
      const paidThisReceipt = getCollectionPaid(coll);
      const paidTillReceipt = getCollectionPaidTillReceipt(coll);
      const discount = getApprovedDiscountForCollection(coll);
      const balance = Math.max(getCollectionBalance(coll), 0);
      const monthNumber = getFeeItemMonth(coll);

      if (!acc[categoryName]) {
        acc[categoryName] = {
          key: categoryName,
          description: categoryName,
          amount_due: 0,
          amount_paid: 0,
          amount_paid_till_receipt: 0,
          discount: 0,
          amount_balanced: 0,
          monthNumbers: new Set(),
          remarks: new Set(),
        };
      }

      acc[categoryName].amount_due += due;
      acc[categoryName].amount_paid += paidThisReceipt;
      acc[categoryName].amount_paid_till_receipt += paidTillReceipt;
      acc[categoryName].discount += discount;
      acc[categoryName].amount_balanced += balance;
      if (monthNumber) acc[categoryName].monthNumbers.add(monthNumber);
      if (coll.remarks) acc[categoryName].remarks.add(coll.remarks);

      return acc;
    }, {});

    return Object.values(groupedItems)
      .map((item) => {
        const monthNumbers = Array.from(item.monthNumbers).sort((a, b) => a - b);
        return {
          ...item,
          monthNumbers,
          monthsLabel: buildMonthLabel(monthNumbers),
          remarksText: Array.from(item.remarks).join(', '),
        };
      })
      .sort((a, b) => {
        const aMonth = a.monthNumbers[0] || 0;
        const bMonth = b.monthNumbers[0] || 0;
        return aMonth - bMonth;
      });
  };

  const getFeeItemTotals = (historyItem = {}) => {
    const feeItems = historyItem.fee_coll_history || [];
    return feeItems.reduce(
      (acc, coll) => {
        const due = getCollectionDue(coll);
        const paid = getCollectionPaid(coll);
        const paidTillReceipt = getCollectionPaidTillReceipt(coll);
        const discount = getApprovedDiscountForCollection(coll);
        const balance = Math.max(getCollectionBalance(coll), 0);

        acc.totalDue += due;
        acc.totalPaid += paid;
        acc.totalPaidTillReceipt += paidTillReceipt;
        acc.totalDiscount += discount;
        acc.totalBalance += balance;
        return acc;
      },
      { totalDue: 0, totalPaid: 0, totalPaidTillReceipt: 0, totalDiscount: 0, totalBalance: 0 }
    );
  };

  const getOverallStatus = (historyItem) => {
    if (historyItem.status?.toLowerCase() === 'refund') return 'Refunded';
    if (historyItem.status?.toLowerCase() === 'adjust') return 'Adjusted';

    const feeItems = historyItem.fee_coll_history || [];
    if (!feeItems.length) return 'Pending';

    const allPaid = feeItems.every(item => {
      return getCollectionBalance(item) <= 0.01;
    });

    const somePaid = feeItems.some(item => {
      const paid = getCollectionPaidTillReceipt(item);
      const discount = getApprovedDiscountForCollection(item);
      return (paid + discount) > 0;
    });

    if (allPaid) return 'Paid';
    if (somePaid) return 'Partially Paid';
    return 'Pending';
  };

  const getPaymentModeIcon = (mode) => {
    const modeIcons = {
      'Cash': FaMoneyBillWave,
      'Card': FaCreditCard,
      'UPI': FaReceipt,
      'Net Banking': FaCreditCard,
      'Cheque': FaReceipt,
      'refund': FaArrowLeft,
      'adjust': FaSync
    };
    return modeIcons[mode] || FaCreditCard;
  };

  const convertAmountToWords = (amount = 0) => {
    const number = Math.round(Number(amount) || 0);
    if (number === 0) return 'Rupees Zero Only';

    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const scales = ['', 'Thousand', 'Lakh', 'Crore', 'Arab'];

    const convertChunk = (num) => {
      let str = '';
      if (num > 99) {
        str += `${ones[Math.floor(num / 100)]} Hundred`;
        num %= 100;
        if (num) str += ' ';
      }
      if (num >= 20) {
        str += tens[Math.floor(num / 10)];
        if (num % 10) str += ` ${ones[num % 10]}`;
      } else if (num >= 10) {
        str += teens[num - 10];
      } else if (num > 0) {
        str += ones[num];
      }
      return str.trim();
    };

    const chunks = [];
    let value = number;
    chunks.push(value % 1000);
    value = Math.floor(value / 1000);
    while (value > 0) {
      chunks.push(value % 100);
      value = Math.floor(value / 100);
    }

    const words = [];
    for (let i = chunks.length - 1; i >= 0; i--) {
      const chunk = chunks[i];
      if (!chunk) continue;
      const chunkWords = convertChunk(chunk);
      const scale = scales[i] ? ` ${scales[i]}` : '';
      words.push(`${chunkWords}${scale}`.trim());
    }

    return `Rupees ${words.join(' ')} Only`;
  };

  const renderTransactionBreakdown = (historyItem) => {
    const refundAudit = historyItem.fee_refund?.refund_audit || [];
    const debited = refundAudit.filter(item => item.type === 'refund');
    const credited = refundAudit.filter(item => item.type === 'adjust');
    const isRefund = historyItem.payment_mode === 'refund' || historyItem.status === 'refund';
    const isAdjustment = historyItem.payment_mode === 'adjust' || historyItem.status === 'adjust';

    let title = 'Transaction Breakdown';
    if (isRefund) title = 'Refund Breakdown';
    if (isAdjustment) title = 'Adjustment Breakdown';

    return (
      <div className="mt-3">
        <div className="receipt-border-box bg-white p-3 rounded border">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold text-primary mb-0">{title}</h6>
            </div>
            
            <Row className="g-3">
                <Col md={credited.length > 0 ? 6 : 12}>
                    <Card className="h-100 border shadow-sm">
                        <Card.Header className="bg-light py-2 px-3">
                            <small className="fw-bold text-danger">{isRefund ? 'REFUNDED FROM' : 'DEBITED FROM (Source)'}</small>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table hover className="mb-0" size="sm" borderless>
                                <tbody>
                                    {debited.map((item, idx) => (
                                        <tr key={idx} className="border-bottom">
                                            <td className="ps-3 py-2 small">
                                              {item.fee_ctgry?.category_name || 'Unknown'}
                                              {item.fee_month && <span className="text-muted ms-1">({getMonthName(item.fee_month)})</span>}
                                            </td>
                                            <td className="text-end pe-3 py-2 small fw-bold text-danger">
                                                {formatCurrency(item.amount)}
                                            </td>
                                        </tr>
                                    ))}
                                    {debited.length === 0 && (
                                        <tr><td colSpan="2" className="text-center text-muted py-2 small">No debit details</td></tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
                
                {credited.length > 0 && (
                  <Col md={6}>
                      <Card className="h-100 border shadow-sm">
                          <Card.Header className="bg-light py-2 px-3">
                              <small className="fw-bold text-success">CREDITED TO (Destination)</small>
                          </Card.Header>
                          <Card.Body className="p-0">
                              <Table hover className="mb-0" size="sm" borderless>
                                  <tbody>
                                      {credited.map((item, idx) => (
                                          <tr key={idx} className="border-bottom">
                                              <td className="ps-3 py-2 small">
                                                {item.fee_ctgry?.category_name || 'Unknown'}
                                                {item.fee_month && <span className="text-muted ms-1">({getMonthName(item.fee_month)})</span>}
                                              </td>
                                              <td className="text-end pe-3 py-2 small fw-bold text-success">
                                                  {formatCurrency(item.amount)}
                                              </td>
                                          </tr>
                                      ))}
                                      {credited.length === 0 && (
                                          <tr><td colSpan="2" className="text-center text-muted py-2 small">No credit details</td></tr>
                                      )}
                                  </tbody>
                              </Table>
                          </Card.Body>
                      </Card>
                  </Col>
                )}
            </Row>
            
             <div className="mt-3 pt-2 border-top text-end">
                <small className="text-muted me-2">Total {isRefund ? 'Refunded' : 'Adjusted'} Amount:</small>
                <span className="fw-bold text-dark">{formatCurrency(historyItem.amount)}</span>
            </div>
        </div>
      </div>
    );
  };

  const getReceiptDetails = (historyItem = {}) => {
    const fallbackName = [userData?.currentUser?.fname, userData?.currentUser?.lname].filter(Boolean).join(' ');
    const studentName =
      historyItem.student_name ||
      getRegistration?.data?.student_full_name ||
      fallbackName ||
      '--';
    const admissionNo =
      getRegistration?.data?.admission_num ||
      '--';
    const classLabel =
      historyItem.class_name ||
      getRegistration?.data?.class?.Classes ||
      historyItem.class ||
      '';
    const sectionLabel =
      historyItem.section_name ||
      getRegistration?.data?.section?.Section ||
      historyItem.section ||
      '';
    const classSection = [classLabel, sectionLabel].filter(Boolean).join(' - ');
    const mobileNo =
      historyItem.mobile_no ||
      getRegistration?.data?.father_mobile_no ||
      getRegistration?.data?.mother_mobile_no ||
      getRegistration?.data?.guardian_mobile_no ||
      userData?.currentUser?.contact_number ||
      '--';
    const feeItems = historyItem.fee_coll_history || [];
    const consolidatedFeeItems = getConsolidatedFeeItems(feeItems);
    const totals = getFeeItemTotals(historyItem);
    const totalDue = totals.totalDue;
    const totalPaid = totals.totalPaid;
    const totalPaidTillReceipt = totals.totalPaidTillReceipt;
    const totalDiscount = totals.totalDiscount;
    const totalBalance = totals.totalBalance;
    const receiptMonthNumbers = Array.from(
      new Set(feeItems.map((item) => getFeeItemMonth(item)).filter(Boolean))
    ).sort((a, b) => a - b);
    const receiptMonthsLabel = buildMonthLabel(receiptMonthNumbers);
    const showDiscountColumn = feeItems.some((item) => hasApprovedDiscountEntry(item));
    const amountInWords = convertAmountToWords(totalPaid || historyItem.amount);
    const referenceNumber =
      historyItem.transaction_no ||
      historyItem.transaction_id ||
      historyItem.payment_reference ||
      historyItem.cheque_no ||
      historyItem.dd_no ||
      historyItem.reference_no ||
      '--';
    const bankName = historyItem.bank_name || historyItem.bank || historyItem.payment_bank || '--';
    const copyLabel = historyItem.copy_label || 'Parent Copy';
    const receiptSession =
      historyItem.session ||
      historyItem.academic_session ||
      getRegistration?.data?.session_data?.Name ||
      moment().format('YYYY');
    const issueDate = historyItem.updated_at || historyItem.payment_date;

    return {
      studentName,
      admissionNo,
      classSection,
      mobileNo,
      feeItems,
      consolidatedFeeItems,
      receiptMonthsLabel,
      showDiscountColumn,
      totalDue,
      totalPaid,
      totalPaidTillReceipt,
      totalDiscount,
      totalBalance,
      amountInWords,
      referenceNumber,
      bankName,
      copyLabel,
      receiptSession,
      issueDate
    };
  };

  const generatePrintableReceiptHTML = (historyItem) => {
    const receiptInfo = getReceiptDetails(historyItem);
    const {
      studentName,
      admissionNo,
      classSection,
      mobileNo,
      consolidatedFeeItems,
      receiptMonthsLabel,
      showDiscountColumn,
      totalDue,
      totalPaid,
      totalPaidTillReceipt,
      totalDiscount,
      totalBalance,
      amountInWords,
      referenceNumber,
      bankName,
      copyLabel,
      receiptSession,
      issueDate
    } = receiptInfo;

    const feeRows = (consolidatedFeeItems.length ? consolidatedFeeItems : [{}]).map((item, idx) => {
      if (!consolidatedFeeItems.length) {
        return `
          <tr>
            <td colspan="${showDiscountColumn ? 7 : 6}" style="text-align:center;padding:12px;">Fee breakup is not available for this receipt.</td>
          </tr>
        `;
      }
      return `
        <tr>
          <td>${idx + 1}</td>
          <td>
            <div style="font-weight:600;">
              ${item.description || 'Fee Item'}
            </div>
            <div style="color:#6b7280;font-size:12px;">Months: ${item.monthsLabel || '--'}</div>
            ${item.remarksText ? `<div style="color:#6b7280;font-size:12px;">${item.remarksText}</div>` : ''}
          </td>
          <td class="text-end">${formatNumber(item.amount_due)}</td>
          <td class="text-end">${formatNumber(item.amount_paid)}</td>
          <td class="text-end">${formatNumber(item.amount_paid_till_receipt)}</td>
          ${showDiscountColumn ? `<td class="text-end">${formatNumber(item.discount)}</td>` : ''}
          <td class="text-end">${formatNumber(item.amount_balanced)}</td>
        </tr>
      `;
    }).join('');

    return `
      <div class="receipt-print-wrapper">
        <div class="receipt-print-border">
          <div class="receipt-print-header">
            <div class="receipt-print-logo">
              ${schoolInfo?.schoolLogo ? `<img src="${schoolInfo.schoolLogo}" alt="School Logo" />` : ''}
            </div>
            <div>
              <h2>${schoolInfo.schoolName || 'School Name'}</h2>
              ${schoolInfo.schoolAddress ? `<p>${schoolInfo.schoolAddress}</p>` : ''}
              <p>Contact No : ${schoolInfo.contact_number || '--'}${schoolInfo.landline_mobile ? ` / ${schoolInfo.landline_mobile}` : ''}</p>
              <p>Email : ${schoolInfo.email || '--'} | Website : ${schoolInfo.websiteUrl || schoolInfo.web_app_link || '--'}</p>
              ${schoolInfo.affiliated ? `<p>${schoolInfo.affiliated}</p>` : ''}
            </div>
            <div class="receipt-copy-label">${copyLabel}</div>
          </div>
          <div class="receipt-print-title">
            <h3>FEE RECEIPT[${receiptSession}]</h3>
          </div>
          <div class="receipt-print-meta">
            <div>
              <div><span>Receipt No.</span><strong>${historyItem.invoice_no || '--'}</strong></div>
              <div><span>Name of the Student</span><strong>${studentName}</strong></div>
              <div><span>Admission No</span><strong>${admissionNo}</strong></div>
            </div>
            <div>
              <div><span>Date</span><strong>${formatDate(historyItem.payment_date)}</strong></div>
              <div><span>Class & Section</span><strong>${classSectionData || '--'}</strong></div>
              <div><span>Mobile No</span><strong>${mobileNo}</strong></div>
              <div><span>Months</span><strong>${receiptMonthsLabel}</strong></div>
            </div>
          </div>
          <table class="receipt-print-table">
            <thead>
              <tr>
                <th style="width:70px;">S.No.</th>
                <th>Description</th>
                <th>Total Due</th>
                <th>Paid This Receipt</th>
                <th>Paid Until This Receipt</th>
                ${showDiscountColumn ? '<th>Discount</th>' : ''}
                <th>Pending Balance</th>
              </tr>
            </thead>
            <tbody>
              ${feeRows}
              <tr class="total-row">
                <td></td>
                <td>Total</td>
                <td>${formatNumber(totalDue)}</td>
                <td>${formatNumber(totalPaid || historyItem.amount)}</td>
                <td>${formatNumber(totalPaidTillReceipt)}</td>
                ${showDiscountColumn ? `<td>${formatNumber(totalDiscount)}</td>` : ''}
                <td>${formatNumber(totalBalance)}</td>
              </tr>
            </tbody>
          </table>
          <div class="receipt-print-section">
            <h4>Pay Mode Information</h4>
            <table class="receipt-print-table">
              <thead>
                <tr>
                  <th style="width:70px;">Sr.No.</th>
                  <th>Pay Mode</th>
                  <th>Bank</th>
                  <th>Number</th>
                  <th>Date</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td>${historyItem.payment_mode || '--'}</td>
                  <td>${bankName}</td>
                  <td>${referenceNumber}</td>
                  <td>${formatDateTime(historyItem.payment_date)}</td>
                  <td>${formatNumber(historyItem.amount || totalPaid)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="receipt-print-amount">
            <div><strong>Total in words :</strong> ${amountInWords}</div>
            <div class="receipt-print-total">Total: ${formatNumber(historyItem.amount || totalPaid)}</div>
          </div>
          <div class="receipt-print-footer">
            <p>This is computer generated receipt, no need of signature</p>
            <span>Issued on ${formatDateTime(issueDate)}</span>
          </div>
        </div>
      </div>
    `;
  };

  const handlePreviewReceipt = (item) => {
    setSelectedReceipt(item);
    setShowModal(true);
  };

  const handlePrintReceipt = (historyItem) => {
    const printWindow = window.open('', '', 'width=900,height=1100');
    if (!printWindow) {
      Swal.fire('Popup Blocked', 'Please allow popups to print the receipt.', 'warning');
      return;
    }

    const printableHTML = generatePrintableReceiptHTML(historyItem);
    const printStyles = `
      body {
        font-family: 'Arial', sans-serif;
        margin: 0;
        padding: 24px;
        background: #f3f4f6;
      }
      .receipt-print-wrapper {
        max-width: 900px;
        margin: 0 auto;
      }
      .receipt-print-border {
        border: 2px solid #111827;
        border-radius: 12px;
        padding: 24px;
        background: #fff;
      }
      .receipt-print-header {
        display: flex;
        justify-content: space-between;
        gap: 16px;
        align-items: flex-start;
      }
      .receipt-print-logo {
        width: 80px;
        height: 80px;
      }
      .receipt-print-logo img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
      .receipt-print-header h2 {
        margin: 0;
        text-transform: uppercase;
        letter-spacing: 1px;
      }
      .receipt-copy-label {
        font-size: 12px;
        text-transform: uppercase;
        font-weight: 600;
        color: #6b7280;
      }
      .receipt-print-title {
        margin-top: 12px;
        text-align: center;
      }
      .receipt-print-title h3 {
        margin: 0;
        letter-spacing: 1px;
      }
      .receipt-print-meta {
        margin-top: 16px;
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
        gap: 16px;
        border: 1px solid #d1d5db;
        padding: 16px;
        border-radius: 8px;
        background: #f9fafb;
      }
      .receipt-print-meta div > div {
        display: flex;
        justify-content: space-between;
        font-size: 14px;
        margin-bottom: 6px;
      }
      .receipt-print-meta span {
        color: #6b7280;
        margin-right: 12px;
      }
      .receipt-print-table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 20px;
        font-size: 14px;
      }
      .receipt-print-table th,
      .receipt-print-table td {
        border: 1px solid #d1d5db;
        padding: 10px;
        text-align: left;
      }
      .receipt-print-table th {
        background: #f1f5f9;
      }
      .receipt-print-table td.text-end,
      .receipt-print-table th.text-end {
        text-align: right;
      }
      .total-row {
        background: #f8fafc;
        font-weight: 600;
      }
      .receipt-print-section h4 {
        margin: 20px 0 10px;
      }
      .receipt-print-amount {
        display: flex;
        justify-content: space-between;
        margin-top: 20px;
        border: 1px dashed #cbd5f5;
        padding: 16px;
        border-radius: 8px;
        background: #f9fafb;
      }
      .receipt-print-total {
        font-size: 18px;
        font-weight: 700;
      }
      .receipt-print-footer {
        display: flex;
        justify-content: space-between;
        margin-top: 12px;
        border-top: 1px dashed #d1d5db;
        padding-top: 12px;
        font-size: 13px;
      }
      @media print {
        body {
          background: #fff;
          padding: 0;
        }
        .receipt-print-border {
          box-shadow: none;
        }
      }
    `;

    printWindow.document.write('<html><head><title>Fee Receipt</title>');
    printWindow.document.write(`<style>${printStyles}</style>`);
    printWindow.document.write('</head><body>');
    printWindow.document.write(printableHTML);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
  };

  const handleToggleRow = (invoiceId) => {
    setExpandedRows((prev) =>
      prev.includes(invoiceId) ? prev.filter((id) => id !== invoiceId) : [...prev, invoiceId]
    );
  };

  // Calculate pagination values
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handleDownloadInvoice = async (invoiceNo, historyItem) => {
    try {
      Swal.fire({
        title: 'Generating Invoice...',
        text: 'Please wait while we prepare your invoice.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Simulate API call for invoice generation
      await new Promise(resolve => setTimeout(resolve, 1500));

      Swal.fire({
        icon: 'success',
        title: 'Invoice Ready!',
        html: `
          <div class="text-start">
            <p><strong>Invoice No:</strong> ${invoiceNo}</p>
            <p><strong>Amount:</strong> ${formatCurrency(historyItem.amount)}</p>
            <p><strong>Date:</strong> ${formatDateTime(historyItem.payment_date)}</p>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Download PDF',
        cancelButtonText: 'Print',
        showDenyButton: true,
        denyButtonText: 'View Details'
      }).then((result) => {
        if (result.isConfirmed) {
          Swal.fire('Downloaded!', 'Invoice PDF has been downloaded.', 'success');
        } else if (result.isDenied) {
          // Navigate to detailed view
          navigate('/invoice-details', { state: { invoiceData: historyItem } });
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          handlePrintReceipt(historyItem);
        }
      });
    } catch (error) {
      Swal.fire('Error', 'Failed to generate invoice.', 'error');
    }
  };

  const getStats = () => {
    const totalTransactions = filteredData.length;
    const totalAmount = filteredData.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    const paidTransactions = filteredData.filter(item => getOverallStatus(item) === 'Paid').length;

    return { totalTransactions, totalAmount, paidTransactions };
  };

  const stats = getStats();

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Header />
          <Container fluid className="py-4">
            <div className="d-flex justify-content-center align-items-center min-vh-50">
              <div className="text-center">
                <Spinner animation="border" variant="primary" size="lg" />
                <p className="mt-3 text-muted">Loading fee history...</p>
              </div>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <Header />
        <Container fluid className="py-4">
          {/* Header Section */}
          <Row className="align-items-center mb-4">
            <Col>
              <div
                className="d-flex align-items-center justify-content-between flex-wrap py-2"
              >
                {/* Icon + Title */}
                <div className="d-flex align-items-center gap-3 flex-grow-1">

                  {/* Icon Circle */}
                  <div
                    className="rounded-circle d-flex justify-content-center align-items-center shadow-sm"
                    style={{
                      width: 58,
                      height: 58,
                      background: "linear-gradient(135deg, #e5edff, #f1f4ff)"
                    }}
                  >
                    <FaHistory className="text-primary" style={{ fontSize: 26 }} />
                  </div>

                  {/* Title + Subtitle */}
                  <div className="d-flex flex-column">
                    <h4 className="fw-bold text-dark mb-1" style={{ fontSize: "1.35rem" }}>
                      Fee Payment History
                    </h4>
                    <span className="text-muted small" style={{ lineHeight: "1.2" }}>
                      Track and review all your past fee transactions
                    </span>
                  </div>
                </div>
              </div>
            </Col>
          </Row>


          {error && (
            <Alert variant="danger" className="border-0 shadow-sm mb-4">
              <div className="d-flex align-items-center">
                <FaExclamationTriangle className="me-3 fs-4" />
                <div className="flex-grow-1">
                  <h6 className="alert-heading mb-1">Error Loading Data</h6>
                  <p className="mb-2">{error}</p>
                  <Button variant="outline-danger" size="sm" onClick={fetchFeeHistory}>
                    <FaSync className="me-1" />
                    Try Again
                  </Button>
                </div>
              </div>
            </Alert>
          )}

          {/* Stats Cards */}
          <Row className="mb-4">
            <div className="stats-scroll-container mb-4">
              <div className="d-flex gap-3 stats-scroll-wrapper">

                {/* Total Transactions */}
                <Card className="border-0 shadow-sm stat-card">
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center">
                      <div className="bg-primary bg-opacity-10 rounded-3 p-3 me-3">
                        <FaReceipt className="text-primary fs-3" />
                      </div>
                      <div>
                        <h3 className="fw-bold text-primary mb-1">{stats.totalTransactions}</h3>
                        <p className="text-muted mb-0">Total Transactions</p>
                      </div>
                    </div>
                  </Card.Body>
                </Card>

                {/* Total Paid Amount */}
                <Card className="border-0 shadow-sm stat-card">
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center">
                      <div className="bg-success bg-opacity-10 rounded-3 p-3 me-3">
                        <FaRupeeSign className="text-success fs-3" />
                      </div>
                      <div>
                        <h3 className="fw-bold text-success mb-1">{formatCurrency(stats.totalAmount)}</h3>
                        <p className="text-muted mb-0">{activeTab === 'refund' ? 'Total Refund Amount' : activeTab === 'adjustment' ? 'Total Adjustment Amount' : 'Total Paid Amount'}</p>
                      </div>
                    </div>
                  </Card.Body>
                </Card>

                {/* Completed Payments */}
                <Card className="border-0 shadow-sm stat-card">
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center">
                      <div className="bg-info bg-opacity-10 rounded-3 p-3 me-3">
                        <FaCheckCircle className="text-info fs-3" />
                      </div>
                      <div>
                        <h3 className="fw-bold text-info mb-1">{stats.paidTransactions}</h3>
                        <p className="text-muted mb-0">Completed Payments</p>
                      </div>
                    </div>
                  </Card.Body>
                </Card>

              </div>
            </div>

          </Row>
          <style jsx>{`
            .custom-tabs .nav-link {
              color: #64748b;
              font-weight: 500;
              border-radius: 8px;
              padding: 10px 20px;
              transition: all 0.2s;
            }
            .custom-tabs .nav-link.active {
              background-color: #0d6efd;
              color: white;
            }
            .custom-tabs .nav-link:hover:not(.active) {
              background-color: #f1f5f9;
              color: #0f172a;
            }

            .stats-scroll-container {
              overflow-x: auto;
              padding-bottom: 5px;
            }

            .stats-scroll-wrapper {
              display: flex;
              flex-wrap: nowrap;
            }

            .stat-card {
              min-width: 260px;   /* scroll ke liye card width fixed */
              flex-shrink: 0;
              border-radius: 16px;
            }

            .fee-receipt-wrapper {
              background: #fff;
              border-radius: 18px;
              padding: 18px;
              box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.08);
            }

            .receipt-border-box {
              border: 2px solid #111827;
              border-radius: 12px;
              padding: 24px;
            }

            .receipt-logo {
              width: 70px;
              height: 70px;
              border: 1px solid #cbd5f5;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 8px;
              background: #f8fafc;
            }

            .receipt-logo img {
              max-width: 100%;
              max-height: 100%;
              object-fit: contain;
            }

            .receipt-meta-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
              gap: 24px;
              border: 1px solid #d1d5db;
              padding: 16px;
              border-radius: 8px;
              background: #f9fafb;
            }

            .receipt-meta-row {
              display: flex;
              justify-content: space-between;
              font-size: 0.9rem;
              margin-bottom: 8px;
              gap: 12px;
            }

            .receipt-meta-row span {
              color: #4b5563;
            }

            .receipt-meta-row strong {
              color: #111827;
            }

            .receipt-table-inner th,
            .receipt-table-inner td {
              vertical-align: middle;
              font-size: 0.92rem;
            }

            .receipt-table-inner thead th {
              background: #f1f5f9;
            }

            .receipt-total-row {
              background: #f8fafc;
            }

            .receipt-amount-words {
              border: 1px dashed #cbd5f5;
              border-radius: 8px;
              padding: 16px;
              background: #f9fafb;
            }

            .receipt-footer-note {
              border-top: 1px dashed #d1d5db;
              padding-top: 12px;
            }

            .receipt-title h6 {
              letter-spacing: 0.08em;
              color: #111827;
            }

            @media (max-width: 768px) {
              .receipt-border-box {
                padding: 16px;
              }

              .receipt-meta-grid {
                grid-template-columns: 1fr;
              }
            }
            `}</style>
          {historyData.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center py-5">
                <FaFileInvoice size={80} className="text-muted mb-4 opacity-25" />
                <h4 className="text-dark mb-3">No Payment History Found</h4>
                <p className="text-muted mb-4">No fee payments have been recorded for this student yet.</p>
                <Button variant="primary" size="lg" className="rounded-pill px-4" onClick={() => navigate('/fee-collection')}>
                  <FaCreditCard className="me-2" />
                  Make First Payment
                </Button>
              </Card.Body>
            </Card>
          ) : (
            <>
              {/* Tabs */}
              <div className="mb-4">
                <Tabs
                  activeKey={activeTab}
                  onSelect={(k) => {
                    setActiveTab(k);
                    setStatusFilter('all');
                    setCurrentPage(1);
                  }}
                  className="mb-3 custom-tabs border-0"
                  fill
                  variant="pills"
                >
                  <Tab eventKey="payment" title="Payments" />
                  <Tab eventKey="refund" title="Refunds" />
                  <Tab eventKey="adjustment" title="Adjustments" />
                </Tabs>
              </div>

              {/* Filters and Search */}
              <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-4">
                  <Row className="g-3">
                    <Col md={6}>
                      <InputGroup>
                        <InputGroup.Text className="bg-light border-end-0">
                          <FaSearch className="text-muted" />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Search by invoice, payment mode, remarks..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="border-start-0"
                        />
                      </InputGroup>
                    </Col>
                    <Col md={3}>
                      <InputGroup>
                        <InputGroup.Text className="bg-light border-end-0">
                          <FaFilter className="text-muted" />
                        </InputGroup.Text>
                        <Form.Select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="all">All Status</option>
                          <option value="Paid">Paid</option>
                          <option value="Partially Paid">Partially Paid</option>
                          <option value="Pending">Pending</option>
                        </Form.Select>
                      </InputGroup>
                    </Col>
                    <Col md={3}>
                      <div className="d-flex gap-2">
                        <Button variant="outline-secondary" className="flex-fill" onClick={fetchFeeHistory}>
                          <FaSync className="me-2" />
                          Refresh
                        </Button>
                        <Button variant="outline-primary" className="flex-fill">
                          <FaDownload className="me-2" />
                          Export
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Transactions List */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Payment Records</h5>
                <span className="text-muted small">
                  Showing {currentItems.length} of {filteredData.length} transactions
                </span>
              </div>

              {filteredData.length === 0 ? (
                <Card className="border-0 shadow-sm">
                  <Card.Body className="text-center py-5">
                    <FaSearch size={48} className="text-muted mb-3 opacity-50" />
                    <h5 className="text-dark mb-2">No transactions found</h5>
                    <p className="text-muted">Try adjusting your search or filter criteria</p>
                  </Card.Body>
                </Card>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {currentItems.map((historyItem, index) => {
                    const isExpanded = expandedRows.includes(historyItem.id);
                    const PaymentModeIcon = getPaymentModeIcon(historyItem.payment_mode);
                    const transactionCount = historyItem.fee_coll_history?.length || 0;
                    const startIndex = (currentPage - 1) * itemsPerPage;
                    const receiptInfo = getReceiptDetails(historyItem);
                    const {
                      studentName,
                      admissionNo,
                      classSection,
                      mobileNo,
                      feeItems,
                      showDiscountColumn,
                      totalDue,
                      totalPaid,
                      totalPaidTillReceipt,
                      totalDiscount,
                      totalBalance,
                      amountInWords,
                      referenceNumber,
                      bankName,
                      copyLabel,
                      receiptSession,
                      issueDate
                    } = receiptInfo;

                    return (
                      <Card className="border-0 shadow-sm" key={historyItem.id}>
                        <Card.Body>
                          <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
                            <div className="d-flex align-items-center gap-3">
                              <div>
                                <h6 className="mb-1 d-flex align-items-center gap-2">
                                  <Badge bg="light" text="primary">#{startIndex + index + 1}</Badge>
                                  {historyItem.invoice_no}
                                  
                                </h6>
                                <div className="text-muted small d-flex flex-wrap gap-3">
                                  <span>
                                    <FaCalendarAlt className="me-1" />
                                    {formatDate(historyItem.payment_date)}
                                  </span>
                                  <span>
                                    <FaRupeeSign className="me-1" />
                                    {formatCurrency(historyItem.amount)}
                                  </span>
                                  <span>
                                    <PaymentModeIcon className="me-1" />
                                    {historyItem.payment_mode}
                                  </span>
                                  <span>
                                    <FaReceipt className="me-1" />
                                    {transactionCount} {transactionCount === 1 ? 'fee item' : 'fee items'}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="text-end">
                              <div className="d-flex gap-2 justify-content-end">
                                <Button
                                  variant={isExpanded ? "outline-primary" : "primary"}
                                  size="sm"
                                  onClick={() => handleToggleRow(historyItem.id)}
                                >
                                  <FaEye className="me-1" />
                                  {isExpanded ? "Hide details" : "View details"}
                                </Button>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  // onClick={() => handleDownloadInvoice(historyItem.invoice_no, historyItem)}
                                  //  onClick={() => handlePrintReceipt(historyItem)}
                                  onClick={() => handlePreviewReceipt(historyItem)}
                                >
                                  <FaFilePdf className="me-1" />
                                  Invoice
                                </Button>
                              </div>
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="mt-4">
                              {(historyItem.payment_mode === 'adjust' ||
                                historyItem.status === 'adjust' ||
                                historyItem.payment_mode === 'refund' ||
                                historyItem.status === 'refund') ? (
                                renderTransactionBreakdown(historyItem)
                              ) : (
                              <div className="fee-receipt-wrapper shadow-sm">
                                <div className="receipt-border-box">
                                  <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start receipt-header">
                                    <div className="d-flex align-items-start gap-3 receipt-school-info">
                                      {schoolInfo?.schoolLogo && (
                                        <div className="receipt-logo">
                                          <img src={schoolInfo.schoolLogo} alt="School Logo" />
                                        </div>
                                      )}
                                      <div>
                                        <h5 className="mb-1 text-uppercase fw-bold">
                                          {schoolInfo.schoolName || 'School Name'}
                                        </h5>
                                        {schoolInfo.schoolAddress && (
                                          <p className="mb-1 small text-muted">{schoolInfo.schoolAddress}</p>
                                        )}
                                        <p className="mb-1 small text-muted">
                                          Contact No : {schoolInfo.contact_number || '--'}
                                          {schoolInfo.landline_mobile ? ` / ${schoolInfo.landline_mobile}` : ''}
                                        </p>
                                        <p className="mb-1 small text-muted">
                                          Email : {schoolInfo.email || '--'} | Website :{' '}
                                          {schoolInfo.websiteUrl || schoolInfo.web_app_link || '--'}
                                        </p>
                                        {schoolInfo.affiliated && (
                                          <p className="mb-0 small text-muted">{schoolInfo.affiliated}</p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-end text-uppercase small fw-semibold text-muted">
                                      {copyLabel}
                                    </div>
                                  </div>

                                  <div className="receipt-title text-center mt-3">
                                    <h6 className="fw-bold mb-0">FEE RECEIPT[{receiptSession}]</h6>
                                  </div>

                                  <div className="receipt-meta-grid mt-3">
                                    <div>
                                      <div className="receipt-meta-row">
                                        <span>Receipt No.</span>
                                        <strong>{historyItem.invoice_no || '--'}</strong>
                                      </div>
                                      <div className="receipt-meta-row">
                                        <span>Name of the Student</span>
                                        <strong>{studentName}</strong>
                                      </div>
                                      <div className="receipt-meta-row">
                                        <span>Admission No</span>
                                        <strong>{admissionNo}</strong>
                                      </div>
                                    </div>
                                    <div>
                                      <div className="receipt-meta-row">
                                        <span>Date</span>
                                        <strong>{formatDate(historyItem.payment_date)}</strong>
                                      </div>
                                      <div className="receipt-meta-row">
                                        <span>Class & Section</span>
                                        <strong>{GetstudentData?.cls_sec?.cls?.Classes} - 
                          {GetstudentData?.cls_sec?.section?.section}</strong>
                                      </div>
                                      <div className="receipt-meta-row">
                                        <span>Mobile No</span>
                                        <strong>{mobileNo}</strong>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="receipt-table mt-4">
                                    <Table bordered responsive className="mb-0 receipt-table-inner">
                                      <thead>
                                        <tr>
                                          <th style={{ width: '70px' }}>S.No.</th>
                                          <th>Description</th>
                                          <th className="text-end" style={{ width: '160px' }}>Total Due</th>
                                          <th className="text-end" style={{ width: '180px' }}>Paid This Receipt</th>
                                          <th className="text-end" style={{ width: '190px' }}>Paid Until This Receipt</th>
                                          {showDiscountColumn && (
                                            <th className="text-end" style={{ width: '150px' }}>Discount</th>
                                          )}
                                          <th className="text-end" style={{ width: '160px' }}>Pending Balance</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {feeItems.length > 0 ? (
                                          feeItems.map((coll, idx) => (
                                            <tr key={coll.id || idx}>
                                              <td>{idx + 1}</td>
                                              <td>
                                                <div className="fw-semibold">
                                                  {coll.fee_coll?.fee_ctgry?.category_name || coll.description || 'Fee Item'}
                                                  {coll.fee_coll?.fee_month && (
                                                    <span className="text-muted ms-1 fw-normal">
                                                      ({getMonthName(coll.fee_coll.fee_month)})
                                                    </span>
                                                  )}
                                                </div>
                                                {coll.remarks && <div className="text-muted small">{coll.remarks}</div>}
                                              </td>
                                              <td className="text-end">{formatNumber(getCollectionDue(coll))}</td>
                                              <td className="text-end">{formatNumber(getCollectionPaid(coll))}</td>
                                              <td className="text-end">{formatNumber(getCollectionPaidTillReceipt(coll))}</td>
                                              {showDiscountColumn && (
                                                <td className="text-end">{formatNumber(getApprovedDiscountForCollection(coll))}</td>
                                              )}
                                              <td className="text-end">
                                                {formatNumber(
                                                  Math.max(getCollectionBalance(coll), 0)
                                                )}
                                              </td>
                                            </tr>
                                          ))
                                        ) : (
                                          <tr>
                                            <td colSpan={showDiscountColumn ? 7 : 6} className="text-center text-muted">
                                              Fee breakup is not available for this receipt.
                                            </td>
                                          </tr>
                                        )}
                                        <tr className="receipt-total-row">
                                          <td />
                                          <td className="fw-bold text-end">Total</td>
                                          <td className="text-end fw-bold">{formatNumber(totalDue)}</td>
                                          <td className="text-end fw-bold">{formatNumber(totalPaid || historyItem.amount)}</td>
                                          <td className="text-end fw-bold">{formatNumber(totalPaidTillReceipt)}</td>
                                          {showDiscountColumn && (
                                            <td className="text-end fw-bold">{formatNumber(totalDiscount)}</td>
                                          )}
                                          <td className="text-end fw-bold">{formatNumber(totalBalance)}</td>
                                        </tr>
                                      </tbody>
                                    </Table>
                                  </div>

                                  <div className="receipt-paymode mt-4">
                                    <h6 className="fw-semibold mb-2">Pay Mode Information</h6>
                                    <Table bordered responsive className="mb-0 receipt-table-inner">
                                      <thead>
                                        <tr>
                                          <th style={{ width: '70px' }}>Sr.No.</th>
                                          <th>Pay Mode</th>
                                          <th>Bank</th>
                                          <th>Number</th>
                                          <th>Date</th>
                                          <th className="text-end">Amount</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr>
                                          <td>1</td>
                                          <td>{historyItem.payment_mode || '--'}</td>
                                          <td>{bankName || '--'}</td>
                                          <td>{referenceNumber}</td>
                                          <td>{formatDateTime(historyItem.payment_date)}</td>
                                          <td className="text-end">{formatNumber(historyItem.amount || totalPaid)}</td>
                                        </tr>
                                      </tbody>
                                    </Table>
                                  </div>

                                  <div className="receipt-amount-words mt-4">
                                    <div className="d-flex flex-column flex-lg-row justify-content-between">
                                      <div className="flex-grow-1 pe-lg-4 mb-3 mb-lg-0">
                                        <strong>Total in words :</strong> {amountInWords}
                                      </div>
                                      <div className="text-lg-end">
                                        <div className="fw-bold fs-5">
                                          Total: {formatNumber(historyItem.amount || totalPaid)}
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="receipt-footer-note mt-3">
                                    <p className="mb-2 small text-muted">
                                      This is computer generated receipt, no need of signature
                                    </p>
                                    <div className="text-end small fw-semibold">
                                      Issued on {formatDateTime(issueDate)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              )}
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <Row className="mt-4">
                  <Col>
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="text-muted">
                        Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredData.length)} of {filteredData.length} entries
                      </div>
                      <Pagination className="mb-0">
                        <Pagination.Prev
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                        />
                        {[...Array(totalPages)].map((_, i) => (
                          <Pagination.Item
                            key={i + 1}
                            active={i + 1 === currentPage}
                            onClick={() => setCurrentPage(i + 1)}
                          >
                            {i + 1}
                          </Pagination.Item>
                        ))}
                        <Pagination.Next
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                        />
                      </Pagination>
                    </div>
                  </Col>
                </Row>
              )}
            </>
          )}
        </Container>
        <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered>
  <Modal.Header closeButton>
    <Modal.Title className="h5 fw-bold">
      Receipt Preview - {selectedReceipt?.invoice_no || '--'}
    </Modal.Title>
  </Modal.Header>
  <Modal.Body className="p-4" style={{ backgroundColor: '#f8f9fa' }}>
    {selectedReceipt && (() => {
      // 1. Calculate / extract variables for selectedReceipt here
      const details = getReceiptDetails ? getReceiptDetails(selectedReceipt) : {};
      
      const copyLabel = details.copyLabel || 'OFFICE COPY';
      const receiptSession = details.receiptSession || '2025-2026';
      const studentName = details.studentName || '--';
      const admissionNo = details.admissionNo || '--';
      const mobileNo = details.mobileNo || '--';
      const feeItems = details.feeItems || details.consolidatedFeeItems || [];
      const showDiscountColumn = details.showDiscountColumn ?? false;
      const totalDue = details.totalDue || 0;
      const totalPaid = details.totalPaid || selectedReceipt.amount || 0;
      const totalPaidTillReceipt = details.totalPaidTillReceipt || 0;
      const totalDiscount = details.totalDiscount || 0;
      const totalBalance = details.totalBalance || 0;
      const bankName = details.bankName || '--';
      const referenceNumber = details.referenceNumber || '--';
      const amountInWords = details.amountInWords || '';
      const issueDate = details.issueDate || selectedReceipt.payment_date;

      return (
        (selectedReceipt.payment_mode === 'adjust' ||
        selectedReceipt.status === 'adjust' ||
        selectedReceipt.payment_mode === 'refund' ||
        selectedReceipt.status === 'refund') ? (
          renderTransactionBreakdown(selectedReceipt)
        ) : (
          <div className="fee-receipt-wrapper shadow-sm">
            <div className="receipt-border-box">
              {/* Header */}
              <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start receipt-header">
                <div className="d-flex align-items-start gap-3 receipt-school-info">
                  {schoolInfo?.schoolLogo && (
                    <div className="receipt-logo">
                      <img src={schoolInfo.schoolLogo} alt="School Logo" />
                    </div>
                  )}
                  <div>
                    <h5 className="mb-1 text-uppercase fw-bold">
                      {schoolInfo?.schoolName || 'School Name'}
                    </h5>
                    {schoolInfo?.schoolAddress && (
                      <p className="mb-1 small text-muted">{schoolInfo.schoolAddress}</p>
                    )}
                    <p className="mb-1 small text-muted">
                      Contact No : {schoolInfo?.contact_number || '--'}
                      {schoolInfo?.landline_mobile ? ` / ${schoolInfo.landline_mobile}` : ''}
                    </p>
                    <p className="mb-1 small text-muted">
                      Email : {schoolInfo?.email || '--'} | Website :{' '}
                      {schoolInfo?.websiteUrl || schoolInfo?.web_app_link || '--'}
                    </p>
                    {schoolInfo?.affiliated && (
                      <p className="mb-0 small text-muted">{schoolInfo.affiliated}</p>
                    )}
                  </div>
                </div>
                <div className="text-end text-uppercase small fw-semibold text-muted">
                  {copyLabel}
                </div>
              </div>

              {/* Title */}
              <div className="receipt-title text-center mt-3">
                <h6 className="fw-bold mb-0">FEE RECEIPT[{receiptSession}]</h6>
              </div>

              {/* Student Meta */}
              <div className="receipt-meta-grid mt-3">
                <div>
                  <div className="receipt-meta-row">
                    <span>Receipt No.</span>
                    <strong>{selectedReceipt.invoice_no || '--'}</strong>
                  </div>
                  <div className="receipt-meta-row">
                    <span>Name of the Student</span>
                    <strong>{studentName}</strong>
                  </div>
                  <div className="receipt-meta-row">
                    <span>Admission No</span>
                    <strong>{admissionNo}</strong>
                  </div>
                </div>
                <div>
                  <div className="receipt-meta-row">
                    <span>Date</span>
                    <strong>{formatDate(selectedReceipt.payment_date)}</strong>
                  </div>
                  <div className="receipt-meta-row">
                    <span>Class & Section</span>
                    <strong>
                      {GetstudentData?.cls_sec?.cls?.Classes} - {GetstudentData?.cls_sec?.section?.section}
                    </strong>
                  </div>
                  <div className="receipt-meta-row">
                    <span>Mobile No</span>
                    <strong>{mobileNo}</strong>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="receipt-table mt-4">
                <Table bordered responsive className="mb-0 receipt-table-inner">
                  <thead>
                    <tr>
                      <th style={{ width: '70px' }}>S.No.</th>
                      <th>Description</th>
                      <th className="text-end" style={{ width: '160px' }}>Total Due</th>
                      <th className="text-end" style={{ width: '180px' }}>Paid This Receipt</th>
                      <th className="text-end" style={{ width: '190px' }}>Paid Until This Receipt</th>
                      {showDiscountColumn && (
                        <th className="text-end" style={{ width: '150px' }}>Discount</th>
                      )}
                      <th className="text-end" style={{ width: '160px' }}>Pending Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feeItems.length > 0 ? (
                      feeItems.map((coll, idx) => (
                        <tr key={coll.id || idx}>
                          <td>{idx + 1}</td>
                          <td>
                            <div className="fw-semibold">
                              {coll.fee_coll?.fee_ctgry?.category_name || coll.description || 'Fee Item'}
                              {coll.fee_coll?.fee_month && (
                                <span className="text-muted ms-1 fw-normal">
                                  ({getMonthName(coll.fee_coll.fee_month)})
                                </span>
                              )}
                            </div>
                            {coll.remarks && <div className="text-muted small">{coll.remarks}</div>}
                          </td>
                          <td className="text-end">{formatNumber(getCollectionDue(coll))}</td>
                          <td className="text-end">{formatNumber(getCollectionPaid(coll))}</td>
                          <td className="text-end">{formatNumber(getCollectionPaidTillReceipt(coll))}</td>
                          {showDiscountColumn && (
                            <td className="text-end">{formatNumber(getApprovedDiscountForCollection(coll))}</td>
                          )}
                          <td className="text-end">
                            {formatNumber(Math.max(getCollectionBalance(coll), 0))}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={showDiscountColumn ? 7 : 6} className="text-center text-muted">
                          Fee breakup is not available for this receipt.
                        </td>
                      </tr>
                    )}
                    <tr className="receipt-total-row">
                      <td />
                      <td className="fw-bold text-end">Total</td>
                      <td className="text-end fw-bold">{formatNumber(totalDue)}</td>
                      <td className="text-end fw-bold">{formatNumber(totalPaid)}</td>
                      <td className="text-end fw-bold">{formatNumber(totalPaidTillReceipt)}</td>
                      {showDiscountColumn && (
                        <td className="text-end fw-bold">{formatNumber(totalDiscount)}</td>
                      )}
                      <td className="text-end fw-bold">{formatNumber(totalBalance)}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>

              {/* Pay mode */}
              <div className="receipt-paymode mt-4">
                <h6 className="fw-semibold mb-2">Pay Mode Information</h6>
                <Table bordered responsive className="mb-0 receipt-table-inner">
                  <thead>
                    <tr>
                      <th style={{ width: '70px' }}>Sr.No.</th>
                      <th>Pay Mode</th>
                      <th>Bank</th>
                      <th>Number</th>
                      <th>Date</th>
                      <th className="text-end">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>1</td>
                      <td>{selectedReceipt.payment_mode || '--'}</td>
                      <td>{bankName}</td>
                      <td>{referenceNumber}</td>
                      <td>{formatDateTime(selectedReceipt.payment_date)}</td>
                      <td className="text-end">{formatNumber(selectedReceipt.amount || totalPaid)}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>

              {/* Amount In Words */}
              <div className="receipt-amount-words mt-4">
                <div className="d-flex flex-column flex-lg-row justify-content-between">
                  <div className="flex-grow-1 pe-lg-4 mb-3 mb-lg-0">
                    <strong>Total in words :</strong> {amountInWords}
                  </div>
                  <div className="text-lg-end">
                    <div className="fw-bold fs-5">
                      Total: {formatNumber(selectedReceipt.amount || totalPaid)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="receipt-footer-note mt-3">
                <p className="mb-2 small text-muted">
                  This is computer generated receipt, no need of signature
                </p>
                <div className="text-end small fw-semibold">
                  Issued on {formatDateTime(issueDate)}
                </div>
              </div>
            </div>
          </div>
        )
      );
    })()}
  </Modal.Body>
  <Modal.Footer>
    <Button variant="secondary" className="me-2" onClick={() => setShowModal(false)}>
      Close
    </Button>
    <Button variant="primary" 
    onClick={() => handlePrintReceipt(selectedReceipt)}
    >
      <FaPrint className="me-1" /> Print Receipt
    </Button>
  </Modal.Footer>
</Modal>

      </div>
    </div>
  );
};

export default FeeHistory;
